import { Tooltip } from '@odo/shared-ui';
import { usePracticeRiskStatuses, resolvePracticeRisk } from '../../hooks/usePracticeRiskStatuses';
import {
    PRACTICE_RISK_ICONS,
    PRACTICE_RISK_TEXT_CLASSES,
    practiceRiskTooltipContent
} from './practiceRisk';
import {
    formatPracticeDoctorLabel,
    type PracticeDoctorLabelFormat
} from './practiceDoctorLabelFormat';

export type { PracticeDoctorLabelFormat };

type TooltipSide = 'top' | 'right' | 'bottom' | 'left';

interface PracticeDoctorLabelProps {
    practiceName?: string | null;
    practiceId?: string | null;
    doctorName?: string | null;
    /** @default 'doctor-practice' */
    format?: PracticeDoctorLabelFormat;
    /** Layout-only classes (size, truncate, spacing) - never a text-color
     *  utility. Color is owned entirely by this component (see
     *  `toneClassName`) so a risk override always replaces exactly one
     *  class instead of racing a caller-supplied color class of equal
     *  Tailwind specificity. */
    className?: string;
    /** Text color applied when the practice is normal risk (no override) -
     *  each call site passes whatever color it originally rendered this text
     *  in. Left undefined to inherit the ambient color instead (matches
     *  sites that never set an explicit color on this text before). */
    toneClassName?: string;
    /** @default 'top' */
    tooltipSide?: TooltipSide;
}

/**
 * Practice/doctor sublabel shared by every "patient + practice" surface in
 * the app - colors the text and swaps in a "{Status}: {note}" tooltip when
 * the practice is flagged warning/at_risk (see
 * hooks/usePracticeRiskStatuses.ts). Renders nothing for an empty label, and
 * renders a plain (uncolored, self-echoing-tooltip) span for a normal-risk
 * practice - visually identical to what every call site rendered before this
 * component existed.
 */
export function PracticeDoctorLabel({
    practiceName,
    practiceId,
    doctorName,
    format = 'doctor-practice',
    className = '',
    toneClassName,
    tooltipSide = 'top'
}: PracticeDoctorLabelProps) {
    const { lookup } = usePracticeRiskStatuses();
    const text = formatPracticeDoctorLabel(format, practiceName, doctorName);
    if (!text) return null;

    const risk = resolvePracticeRisk(lookup, { practiceId, practiceName });
    const colorClass = risk ? PRACTICE_RISK_TEXT_CLASSES[risk.riskStatus] : (toneClassName ?? '');
    const tooltipContent = risk ? practiceRiskTooltipContent(risk) : text;
    const RiskIcon = risk ? PRACTICE_RISK_ICONS[risk.riskStatus] : null;

    return (
        <Tooltip content={tooltipContent} side={tooltipSide}>
            <span className={[colorClass, className].filter(Boolean).join(' ')}>
                {RiskIcon && (
                    <RiskIcon
                        className="inline-block h-3.5 w-3.5 mr-1 align-middle"
                        aria-hidden="true"
                    />
                )}
                {text}
            </span>
        </Tooltip>
    );
}
